import React from 'react'
import { Info } from 'lucide-react'
import { WardInfo } from '@/lib/xlsx-loader'
import { Language, t } from '@/lib/i18n'
import { buildWhatsAppImageRequestMessage } from '@/lib/whatsapp-utils'
import { extractItemId, generateId } from '@/lib/engagementId'
import { SmartImage } from '@/components/ui/SmartImage'
import { LeaderProfileCard } from '@/components/gram-panchayat/LeaderProfileCard'
import { QuickLinksStrip } from './QuickLinksStrip'

interface WardListProps {
  wards: WardInfo[] | undefined
  lang: Language
  district: string
  block: string
  village: string
  pathDistrict?: string
  pathBlock?: string
  pathVillage?: string
  districtName?: string
  blockName?: string
  villageName?: string
  level?: 'gp' | 'bs' | 'zp'
  isFormerTenure?: boolean
}

export const WardList: React.FC<WardListProps> = ({
  wards: initialWards,
  lang,
  district,
  block,
  village,
  pathDistrict,
  pathBlock,
  pathVillage,
  districtName,
  blockName,
  villageName,
  level = 'gp',
  isFormerTenure = false
}) => {
  // Dynamic minimum wards based on administrative level
  const minWardsMap = {
    gp: 0,
    bs: 10,
    zp: 10
  }
  const minWards = minWardsMap[level] ?? 6

  const sanitizeForPath = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const dPath = sanitizeForPath(pathDistrict || district)
  const bPath = sanitizeForPath(pathBlock || block)
  const vPath = sanitizeForPath(pathVillage || village)

  const maxWardWithData = (initialWards || []).reduce((max, w) => {
    const has = !!(String(w.en || "").trim() || String(w.hi || "").trim() || String(w.pa || "").trim())
    if (!has) return max
    return Math.max(max, Number(w.number) || 0)
  }, 0)
  const targetCount = Math.max(minWards, maxWardWithData)

  // Generate full list of wards including holes and padding
  const wards: WardInfo[] = []
  for (let i = 1; i <= targetCount; i++) {
    const existing = initialWards?.find(w => w.number === i)
    if (existing) {
      wards.push(existing)
    } else {
      wards.push({
        number: i,
        en: "",
        hi: "",
        pa: ""
      })
    }
  }

  const getImagePath = (wardNum: number) => {
    if (level === 'zp') return `/images/zp/${dPath}/ward_${wardNum}.jpg`
    if (level === 'bs') return `/images/bs/${dPath}/${bPath}/ward_${wardNum}.jpg`
    return `/images/gp/${dPath}/${bPath}/${vPath}/ward_${wardNum}.jpg`
  }

  const getDesignationLabel = (num: number) => {
    if (level === 'bs') return `${t("block_samiti_member_label", lang)} ${num}`
    if (level === 'zp') return `${t("zila_parishad_member_label", lang)} ${num}`
    return `${t("panch_ward_prefix", lang)} ${num}`
  }

  const buildWardWhatsAppMessage = (w: WardInfo) => {
    const rawName = String((lang === "en" ? w.en : lang === "hi" ? w.hi : w.pa) || "").trim()
    const safeName = rawName || t("whatsapp_name_missing", lang)
    const designation = getDesignationLabel(w.number)

    const gpVal = lang === "en" ? (villageName || village) : (villageName || "")
    const blockVal = lang === "en" ? (blockName || block) : (blockName || "")
    const districtVal = lang === "en" ? (districtName || district) : (districtName || "")

    return buildWhatsAppImageRequestMessage({
      lang,
      name: safeName,
      designation,
      gp: gpVal,
      block: blockVal,
      district: districtVal,
      isFormerTenure
    })
  }

  const buildMissingDataWhatsAppMessage = (w: WardInfo) => {
    // Use same template for missing data, leaving name empty
    return buildWardWhatsAppMessage(w)
  }

  const buildWhatsAppHref = (w: WardInfo, hasData: boolean) => {
    const msg = hasData ? buildWardWhatsAppMessage(w) : buildMissingDataWhatsAppMessage(w)
    const encoded = encodeURIComponent(msg)
    return `https://wa.me/919466333211?text=${encoded}`
  }

  const handleWhatsAppClick = (e: React.MouseEvent<HTMLAnchorElement>, w: WardInfo, hasData: boolean) => {
    e.preventDefault()
    try {
      if (!district || !block || !village) {
        alert('Please select District, Block, and Village before submitting.')
        return
      }

      const webUrl = buildWhatsAppHref(w, hasData)
      window.open(webUrl, "_blank", "noopener,noreferrer")
    } catch {
      const webUrl = buildWhatsAppHref(w, hasData)
      window.open(webUrl, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <div className="w-full ward-members-section" data-ward-members="true">
      <div className="mb-6 border-b border-border pb-4 flex flex-wrap items-center justify-between gap-3 px-2 ward-members-header">
        <div className="flex-1" aria-hidden="true" />
        <h3 className="heading-secondary text-foreground text-center flex-none ward-members-title">
          {lang === 'hi' ? 'वार्ड सदस्य' : lang === 'pa' ? 'ਵਾਰਡ ਮੈਂਬਰ' : 'Ward Members'}
        </h3>
        <div className="flex-1" aria-hidden="true" />
      </div>
      <div
        id="ward-members-index-wrapper"
        className="overflow-hidden"
      >
        <div id="ward-members-index" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {wards.map((ward) => {
            const hasData = !!(ward.en || ward.hi || ward.pa);
            const wardImageSrc = hasData ? getImagePath(ward.number) : "/images/placeholder.svg"
            const linkHref = buildWhatsAppHref(ward, hasData);
            const wardId = `ward-${level}-${dPath}-${bPath}-${vPath}-${ward.number}`
            const item = { id: wardId, level, district: dPath, block: bPath, village: vPath, wardNumber: ward.number }
            console.log("FULL ITEM:", item)
            const itemId = String(extractItemId(item) || generateId(item))
            console.log("FINAL ITEM ID:", itemId)

            return (
              <LeaderProfileCard
                key={ward.number}
                id={wardId}
                engagementKey={itemId}
                engagementType="ward"
                level={level}
                tenure={isFormerTenure ? "previous" : "current"}
                position="ward-member"
                wardNumber={ward.number}
                district={dPath}
                block={bPath}
                village={vPath}
                engagementDisabled={!hasData}
                className="card p-4 hover:shadow-md transition-shadow max-w-full"
                name={hasData ? (
                  <span
                    className="font-bold text-[length:var(--app-font-size)] text-foreground text-center leading-tight px-0 block whitespace-normal break-words w-full"
                    title={(ward[lang] || ward.en || t("data_unavailable", lang))}
                  >
                    {ward[lang] || ward.en || t("data_unavailable", lang)}
                  </span>
                ) : (
                  <a
                    href={linkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleWhatsAppClick(e, ward, hasData)}
                    className="font-bold text-[length:var(--app-font-size)] text-destructive italic text-center leading-tight px-0 block whitespace-normal break-words w-full hover:underline"
                  >
                    {t("data_unavailable", lang)}
                  </a>
                )}
                designation={getDesignationLabel(ward.number)}
                alt={`${t("panch_ward_prefix", lang)} ${ward.number}`}
                customImageRenderer={() => (
                  <div className="relative w-full aspect-square max-w-[170px] rounded-xl border-[3px] border-black p-[5px] bg-white shadow-lg overflow-hidden transition-transform duration-300 group">
                    <div className="relative w-full h-full rounded-lg bg-[#87CEEB] overflow-hidden">
                      <SmartImage
                        src={wardImageSrc}
                        alt={`${t("panch_ward_prefix", lang)} ${ward.number}`}
                        className="w-full h-full object-cover"
                        lazy={true}
                        fallbackSrc="/images/placeholder.svg"
                        showAltOnError={false}
                      />
                      <a
                        href={linkHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleWhatsAppClick(e, ward, hasData)}
                        className={`absolute inset-0 flex flex-col items-center justify-center text-center px-2 z-10 transition-colors ${hasData
                          ? "sarpanch-whatsapp-link ward-box"
                          : "text-primary hover:bg-card/90 font-bold text-sm bg-card/80"
                          }`}
                        aria-label={hasData ? "Click to submit your Ward Member image via WhatsApp" : "Click to submit your details via WhatsApp"}
                      >
                        {hasData ? (
                          <>
                            <span className="block leading-none mb-1">{t("image_prompt_l1", lang)}</span>
                            <span className="block leading-none mb-1">{t("image_prompt_l2", lang)}</span>
                            <span className="block leading-none">{t("image_prompt_l3", lang)}</span>
                          </>
                        ) : (
                          t("ward_data_unavailable_btn", lang)
                        )}
                      </a>
                    </div>
                  </div>
                )}
              />
            )
          })}
        </div>

        <div className="w-full flex justify-between items-start mt-2 px-1">
          {/* Disclaimer with Hover Tooltip */}
          <div className="relative group">
            <div className="flex items-center gap-1 cursor-help text-muted-foreground hover:text-foreground transition-colors">
              <Info className="w-3.5 h-3.5" />
              <span className="text-xs font-medium border-b border-dotted border-muted-foreground/50">
                {t("disclaimer_label", lang)}
              </span>
            </div>

            {/* Tooltip Content */}
            <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-popover text-popover-foreground text-xs rounded-md shadow-md border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 pointer-events-none">
              {t("disclaimer_text", lang)}
              <div className="absolute -bottom-1 left-4 w-2 h-2 bg-popover border-b border-r border-border transform rotate-45"></div>
            </div>
          </div>

          <span className="text-sm text-muted-foreground italic font-medium">
            {t("consensus_label", lang)}
          </span>
        </div>
      </div>
      <QuickLinksStrip level={level} />
    </div>
  )
}
